import { api } from './api';

/**
 * Teto de tamanho de arquivo — fonte única do frontend.
 *
 * Spec: jurisdicao_webwidget/specs/0004-upload-de-arquivos-grandes (AC-1, AC-2)
 *
 * Era 25 MB, duplicado aqui e em `pages/folders/details/index.tsx`. Os 60 MB só passam porque o
 * upload deixou de atravessar a Lambda (que crava o request em 6 MB) e vai direto ao S3.
 *
 * ⚠️ Esta guarda é de UX — dar mensagem clara antes de gastar a banda do usuário. **Ela não é a
 * proteção:** quem impõe o teto é a policy do presigned POST, no S3, que recusa 61 MB mesmo que o
 * cliente seja adulterado.
 */
export const MAX_FILE_SIZE_BYTES = 60 * 1024 * 1024;
export const MAX_FILE_SIZE_LABEL = '60MB';

export interface Folder {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  createdBy: string;
  organizationId: string;
  fileCount: number;
  totalSize: number;
  permissions: Array<{
    userId: string;
    userName: string;
    level: 'read' | 'write' | 'admin';
  }>;
}

export interface FileItem {
  id: string;
  name: string;
  originalName: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  folderId: string;
  path: string;
}

export interface CreateFolderRequest {
  name: string;
  description?: string;
}

export interface UpdateFolderRequest {
  name?: string;
  description?: string;
}

export interface FolderPermission {
  userId: string;
  level: 'read' | 'write' | 'admin';
}

class FoldersService {
  private baseUrl = '/api/folders';

  // Gerenciamento de Pastas
  async getFolders(): Promise<{ data: Folder[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(this.baseUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Se o backend retorna diretamente o array, não um objeto com data
      const folders = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('Pastas recebidas:', folders);
      
      return { data: folders };
    } catch (error) {
      console.error('Erro ao buscar pastas:', error);
      throw error;
    }
  }

  // Novos endpoints para usuário final
  async getMyFolders(): Promise<{ data: Folder[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get('/api/folders/my-folders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const folders = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('Minhas pastas:', folders);
      
      return { data: folders };
    } catch (error) {
      console.error('Erro ao buscar minhas pastas:', error);
      throw error;
    }
  }

  async getMyPermissions(): Promise<{ data: any[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get('/api/folders/my-permissions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const permissions = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: permissions };
    } catch (error) {
      console.error('Erro ao buscar minhas permissões:', error);
      throw error;
    }
  }

  async getFoldersByPermission(permission: 'read' | 'write' | 'admin'): Promise<{ data: Folder[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`/api/folders/by-permission/${permission}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const folders = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: folders };
    } catch (error) {
      console.error(`Erro ao buscar pastas com permissão ${permission}:`, error);
      throw error;
    }
  }

  async getFoldersWithFilter(filter?: 'accessible', permission?: 'read' | 'write' | 'admin'): Promise<{ data: Folder[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      
      let url = this.baseUrl;
      const params = new URLSearchParams();
      
      if (filter) params.append('filter', filter);
      if (permission) params.append('permission', permission);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const folders = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: folders };
    } catch (error) {
      console.error('Erro ao buscar pastas com filtro:', error);
      throw error;
    }
  }

  async getFolderById(id: string): Promise<{ data: Folder }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`${this.baseUrl}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Se o backend retorna diretamente o objeto, não um objeto com data
      const folder = response.data.data || response.data;
      console.log('Pasta por ID recebida:', folder);
      
      return { data: folder };
    } catch (error) {
      console.error('Erro ao buscar pasta:', error);
      throw error;
    }
  }

  async createFolder(data: CreateFolderRequest): Promise<{ data: Folder }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.post(this.baseUrl, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao criar pasta:', error);
      throw error;
    }
  }

  async updateFolder(id: string, data: UpdateFolderRequest): Promise<{ data: Folder }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.put(`${this.baseUrl}/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar pasta:', error);
      throw error;
    }
  }

  async deleteFolder(id: string): Promise<void> {
    try {
      const token = localStorage.getItem('@App:token');
      await api.delete(`${this.baseUrl}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Erro ao excluir pasta:', error);
      throw error;
    }
  }

  // Gerenciamento de Arquivos
  async getFolderFiles(folderId: string): Promise<{ data: FileItem[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`${this.baseUrl}/${folderId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Se o backend retorna diretamente o array, não um objeto com data
      const files = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('Arquivos da pasta recebidos:', files);
      
      return { data: files };
    } catch (error) {
      console.error('Erro ao buscar arquivos da pasta:', error);
      throw error;
    }
  }

  /**
   * Upload em três passos, direto ao S3 — é o que permite 60 MB.
   *
   * Spec: jurisdicao_webwidget/specs/0004-upload-de-arquivos-grandes · ADR-0011
   *
   * O caminho antigo mandava o arquivo para a API, e a Lambda Function URL crava o request em 6 MB
   * (limite da AWS, não configurável). Aqui o byte vai do browser direto ao S3 e a API só vê a chave.
   *
   * ⚠️ **Fallback proposital:** se o backend ainda não tiver as rotas novas (404 no `upload-intent`),
   * cai para o multipart antigo. Sem isso, publicar este frontend antes do backend quebraria TODO
   * upload, inclusive os pequenos que funcionam hoje — e ordem de deploy já derrubou produção neste
   * projeto (ver o item de ordem widget/host no STATE).
   */
  async uploadFile(folderId: string, file: File): Promise<{ data: FileItem }> {
    const token = localStorage.getItem('@App:token');

    let autorizacao: { url: string; fields: Record<string, string>; key: string };
    try {
      const intent = await api.post(
        `${this.baseUrl}/${folderId}/upload-intent`,
        { fileName: file.name, contentType: file.type, size: file.size },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      autorizacao = intent.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.warn('upload-intent indisponível; usando o caminho antigo (limite ~4,5 MB)');
        return this.uploadFileLegado(folderId, file, token);
      }
      console.error('Erro ao autorizar upload:', error?.response?.data ?? error);
      throw error;
    }

    /*
     * Envio ao S3. `fetch` puro de propósito: a instância `api` tem baseURL e o header de
     * Authorization não pode vazar para a AWS. E o campo `file` vai POR ÚLTIMO — o S3 ignora
     * qualquer campo que venha depois dele.
     */
    const formData = new FormData();
    Object.entries(autorizacao.fields).forEach(([k, v]) => formData.append(k, v));
    formData.append('file', file);

    const envio = await fetch(autorizacao.url, { method: 'POST', body: formData });
    if (!envio.ok) {
      const detalhe = await envio.text().catch(() => '');
      console.error('S3 recusou o upload:', envio.status, detalhe.slice(0, 300));
      throw new Error(
        envio.status === 403
          ? `Arquivo recusado pelo servidor. Máximo ${MAX_FILE_SIZE_LABEL}.`
          : `Falha ao enviar o arquivo (HTTP ${envio.status}).`
      );
    }

    // Passo 3: sem isto o objeto existe no S3 e não existe registro no banco.
    const confirmacao = await api.post(
      `${this.baseUrl}/${folderId}/upload-complete`,
      { key: autorizacao.key, originalName: file.name },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return { data: confirmacao.data };
  }

  /** Caminho antigo (multipart pela API). Mantido como fallback — teto efetivo de ~4,5 MB. */
  private async uploadFileLegado(
    folderId: string,
    file: File,
    token: string | null
  ): Promise<{ data: FileItem }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId);

    const response = await api.post(`${this.baseUrl}/${folderId}/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async downloadFile(fileId: string, fileName: string): Promise<void> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`/api/files/${fileId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Criar link para download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do arquivo:', error);
      throw error;
    }
  }

  async deleteFile(fileId: string): Promise<void> {
    try {
      const token = localStorage.getItem('@App:token');
      await api.delete(`/api/files/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      throw error;
    }
  }

  // Gerenciamento de Usuários
  async getAvailableUsers(): Promise<{ data: any[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get('/api/folders/users/available', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const users = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: users };
    } catch (error) {
      console.error('Erro ao buscar usuários disponíveis:', error);
      throw error;
    }
  }

  async searchUsers(search: string): Promise<{ data: any[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`/api/folders/users/search?search=${encodeURIComponent(search)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const users = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: users };
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }

  // Gerenciamento de Permissões (Atualizados para o backend)
  async getFolderPermissions(folderId: string): Promise<{ data: FolderPermission[] }> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.get(`${this.baseUrl}/${folderId}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const permissions = Array.isArray(response.data) ? response.data : response.data.data || [];
      return { data: permissions };
    } catch (error) {
      console.error('Erro ao buscar permissões da pasta:', error);
      throw error;
    }
  }

  async addUserToFolderById(folderId: string, userId: string, level: 'read' | 'write' | 'admin'): Promise<any> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.post(`${this.baseUrl}/${folderId}/permissions/user`, {
        userId,
        level
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar usuário à pasta:', error);
      throw error;
    }
  }

  async addUserToFolderByEmail(folderId: string, userEmail: string, level: 'read' | 'write' | 'admin'): Promise<any> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.post(`${this.baseUrl}/${folderId}/permissions/user`, {
        userEmail,
        level
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar usuário à pasta por email:', error);
      throw error;
    }
  }

  async addMultipleUsersToFolder(folderId: string, users: Array<{ userId?: string; userEmail?: string; level: 'read' | 'write' | 'admin' }>): Promise<any> {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await api.post(`${this.baseUrl}/${folderId}/permissions/bulk`, {
        users
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar múltiplos usuários à pasta:', error);
      throw error;
    }
  }

  async removeUserFromFolder(folderId: string, userId: string): Promise<void> {
    try {
      const token = localStorage.getItem('@App:token');
      await api.delete(`${this.baseUrl}/${folderId}/permissions/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Erro ao remover usuário da pasta:', error);
      throw error;
    }
  }

  // Validações
  validateFile(file: File): { isValid: boolean; error?: string } {
    const allowedExtensions = ['.txt', '.docx', '.pdf', '.png', '.jpg', '.jpeg'];
    const blockedExtensions = ['.exe', '.bat', '.cmd', '.msi', '.scr', '.com', '.pif', '.vbs', '.js', '.jar'];
    
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    
    // Verificar extensões bloqueadas
    if (blockedExtensions.some(ext => fileName.endsWith(ext))) {
      return {
        isValid: false,
        error: `Arquivo ${extension} não é permitido por questões de segurança.`
      };
    }
    
    // Verificar extensões permitidas
    if (!allowedExtensions.some(ext => fileName.endsWith(ext))) {
      return {
        isValid: false,
        error: `Apenas arquivos ${allowedExtensions.join(', ')} são permitidos.`
      };
    }
    
    // Verificar tamanho (ver MAX_FILE_SIZE_BYTES — o teto real é imposto pela policy do S3)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        isValid: false,
        error: `Arquivo muito grande. Tamanho máximo: ${MAX_FILE_SIZE_LABEL}.`
      };
    }
    
    return { isValid: true };
  }

  // Utilitários
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const foldersService = new FoldersService(); 